import { useMemo, useState } from 'react';
import { teamFilter } from '../use-teams';
import type { LeaveTimeline, LeaveType } from '@forecastify/shared';
import {
  AvatarFace,
  Gantt,
  Icon,
  ListToolbar,
  type GanttBar,
  type GanttRow,
  type GanttUnit,
  useIsMobile,
} from '@forecastify/ui';
import { useT } from '../../i18n';
import { useCalendarLabels } from '../date-field';
import { identityColor, initialsOf } from '../identity';
import { useAvatars } from '../use-avatar';
import { RangeControl } from './range-control';
import { PeopleGridSkeleton } from './grid-skeleton';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ SZABADSÁGOK — the mockup's `dashleave`. Who is away, and when.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **IT IS THE TASK TIMELINE'S CHART NOW, AND IT WAS A SECOND ONE — 15 Sep.** This screen had
 * its own hand-rolled timeline: its own day widths, its own sticky column, its own month strip, its
 * own today line. All of it worked and none of it zoomed — the tasks timeline had been rebuilt onto
 * Jira's unit model (day columns, month columns, quarter columns) and this one was still a day
 * ruler that got narrower. Two timelines in one product is two places for every fix.
 *
 * ⚠️⚠️ **THE CHART NEEDED THREE ADDITIONS TO TAKE THIS SCREEN, AND THEY ARE FLAGGED IN `gantt.tsx`
 * RATHER THAN HIDDEN HERE.** A task row has one pair of dates; a PERSON row has as many bands as
 * they have requests (`GanttRow.bars`). A task row's name cell is a key and a title; a person's is
 * a face and a name (`GanttRow.lead`). And a day the calendar has taken is not a day somebody chose
 * off (`GanttProps.columnTint`), with the away-count above each column (`columnStrip`) — the figure
 * somebody scans before booking anything.
 *
 * ⚠️ **Pending bands are outlines rather than hidden**: a planner deciding next month's work needs
 * to see a request nobody has answered yet, and hiding it makes the plan look emptier than it is.
 *
 * ⚠️ **A band is a button** — clicking one opens that request in the drawer, because *"who is away
 * on the 24th"* is always followed by *"and what is it"*.
 */

/**
 * ⚠️ The three settings, and they are the tasks timeline's own table — a screen that zoomed
 * differently from the other timeline would be the same divergence in a smaller place. `hét` draws
 * DAY columns, which is what Jira's *Weeks* does and what `gantt.tsx` argues for.
 */
const ZOOMS = {
  week: { unitWidth: 30, unit: 'day', months: 3 },
  month: { unitWidth: 200, unit: 'month', months: 12 },
  quarter: { unitWidth: 200, unit: 'quarter', months: 24 },
} as const satisfies Record<string, { unitWidth: number; unit: GanttUnit; months: number }>;

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ A ZOOM CHANGES THE WINDOW TOO, AND NOT DOING SO MADE THE CHART A STRIP.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Reported 15 Sep, in the strongest terms available: choosing *negyedév* over a two-week window
 * drew ONE quarter column, 200px wide, with every band crushed inside it and three quarters of the
 * screen blank. The chart was right — a quarter unit over fourteen days is one column — and the
 * question was wrong: the unit had been made a display setting over a window somebody else chose.
 *
 * ⚠️ **So the zoom names a SPAN as well as a unit**, and picking one moves the window to it: three
 * months of days, a year of months, two years of quarters. The chart is then wider than the screen
 * at every zoom, which is what makes it scroll — and the period arrows step it, because they step
 * by the window's own length.
 *
 * ⚠️ **Anchored on the first day of the window's own month**, not on today: somebody who has walked
 * back to March and then zooms out wants March in view, not this week.
 */
function spanFor(zoom: keyof typeof ZOOMS, from: string): { from: string; to: string } {
  const year = Number(from.slice(0, 4));
  const month = Number(from.slice(5, 7)) - 1;
  const start = new Date(Date.UTC(year, month, 1));
  /* ⚠️ Day zero of the month AFTER the span is the last day of the span — the leap-year rule for
     free, and the spelling `leave-frame.ts` uses for the same reason. */
  const end = new Date(Date.UTC(year, month + ZOOMS[zoom].months, 0));
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}

export function LeaveTimelineScreen(props: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  /** The period requested — shown by the control at once (14 Oct 2026). Absent, the timeline's. */
  readonly range?: { readonly from: string; readonly to: string };
  readonly timeline: LeaveTimeline;
  readonly types: readonly LeaveType[];
  readonly managers: readonly { readonly id: string; readonly name: string }[];
  readonly managerId: string;
  readonly leaveTypeId: string;
  readonly status: string;
  readonly query: string;
  readonly today: string;
  readonly defaultRange: { readonly from: string; readonly to: string };
  readonly onManager: (id: string) => void;
  /** R-60 · the team filter — absent where the screen is embedded without one. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly teamId?: string;
  readonly onTeam?: (id: string) => void;
  readonly onLeaveType: (id: string) => void;
  readonly onStatus: (status: string) => void;
  readonly onQuery: (value: string) => void;
  readonly onRange: (from: string, to: string) => void;
  readonly onOpenRequest: (id: string) => void;
  /**
   * ⚠️ U-11 · whose bands open their request. Everybody sees every band; only somebody holding
   * `leave.view`, or the band's own person, opens it — the server refuses the rest anyway, and a
   * band that opens onto a refusal is a band that lies about being clickable.
   */
  readonly canOpen: (memberId: string) => boolean;
}) {
  const mobile = useIsMobile();
  const t = useT();
  const calendar = useCalendarLabels();
  const [zoom, setZoom] = useState<keyof typeof ZOOMS>('week');
  /* ⚠️ The same cache every other list reads — one fetch per picture for the whole app. */
  const avatars = useAvatars(props.timeline.rows.map((row) => row.avatarUrl));

  const holidays = useMemo(() => new Set(props.timeline.holidays), [props.timeline.holidays]);

  /*
   * ⚠️⚠️ **`awayOn` WENT WITH THE COLUMN STRIP — round 5 item 7.** It counted how many people
   * were away on a day, folded to *the worst day in the column*, and fed the digit that floated
   * over each date. With the strip gone it had one caller and no other, so keeping it would be a
   * function the screen computes for nobody. The reasoning is in the note beside `columnStrip`'s
   * removal, which is where anybody restoring the figure will look.
   */

  /* ⚠️ Which keys are BANDS — see `onOpen` below, which is called with a row's key as well. */
  const requestIds = new Set(
    props.timeline.rows
      .filter((row) => props.canOpen(row.memberId))
      .flatMap((row) => row.bands.map((band) => band.requestId)),
  );

  const rows: GanttRow[] = props.timeline.rows.map((row) => {
    const bars: GanttBar[] = row.bands.map((band) => ({
      key: band.requestId,
      title: band.leaveTypeName,
      start: band.fromDate,
      end: band.toDate,
      /* ⚠️ The person's identity colour, so one row reads as one person's absences rather than as
         a stripe of the accent shared with everybody else's. */
      barColor: identityColor(row.memberId),
      barLabel: `${band.fromDate.slice(5)} – ${band.toDate.slice(5)}`,
      ...(band.status === 'pending' ? { barDashed: true } : {}),
    }));

    const picture = avatars.get(row.avatarUrl ?? '');

    return {
      key: row.memberId,
      title: row.memberName,
      /* ⚠️ The ENVELOPE — `GanttRow.bars` says why it is still required. Falls back to the window
         when somebody has no bands at all, which cannot draw anything either way. */
      start: row.bands[0]?.fromDate ?? props.timeline.from,
      end: row.bands[row.bands.length - 1]?.toDate ?? props.timeline.from,
      bars,
      lead: (
        <>
          {picture === undefined ? (
            <span
              aria-hidden
              className="text-on-id grid size-6 shrink-0 place-items-center rounded-full text-[9.5px] font-semibold"
              style={{ background: identityColor(row.memberId) }}
            >
              <AvatarFace
                seed={row.avatarSeed ?? undefined}
                fallback={initialsOf(row.memberName)}
                size={24}
              />
            </span>
          ) : (
            <img src={picture} alt="" className="size-6 shrink-0 rounded-full object-cover" />
          )}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12px] font-medium">{row.memberName}</span>
            {/* ⚠️ No line rather than an em-dash — the logging grid's own note argues it. */}
            {row.role === null ? null : (
              <span className="text-muted-foreground block truncate text-[10.5px]">{row.role}</span>
            )}
          </span>
        </>
      ),
    };
  });

  return (
    <div data-screen="dash-leave" className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b pr-3">
        <ListToolbar
          className="flex-1 border-b-0"
          labels={{
            search: t('dash.leave.search'),
            filter: t('list.filter'),
            views: t('list.views'),
            columns: t('list.columns'),
            clearAll: t('list.clearAll'),
            searchOptions: t('list.searchOptions'),
            clearFilter: (label: string) => t('list.clearFilter', { label }),
            rangeFrom: t('list.rangeFrom'),
            rangeTo: t('list.rangeTo'),
            switchView: t('list.switchView'),
            more: t('list.more'),
            back: t('list.back'),
            close: t('common.close'),
          }}
          search={props.query}
          onSearchChange={props.onQuery}
          /* ⚠️ The zoom sits where the tasks timeline puts it — in the toolbar's view switch, with
             the same three words. */
          views={[
            { value: 'week', label: t('tasks.zoom.week') },
            { value: 'month', label: t('tasks.zoom.month') },
            { value: 'quarter', label: t('tasks.zoom.quarter') },
          ]}
          view={zoom}
          onViewChange={(value) => {
            const next = value === 'month' ? 'month' : value === 'quarter' ? 'quarter' : 'week';
            setZoom(next);
            /* ⚠️ The window follows the unit — `spanFor` argues why, and the alternative is the
               one-column strip this screen shipped as. */
            const span = spanFor(next, props.timeline.from);
            props.onRange(span.from, span.to);
          }}
          filters={[
            {
              key: 'manager',
              label: t('dash.filter.manager'),
              value: props.managerId,
              onChange: props.onManager,
              options: [
                { value: '', label: t('dash.filter.everybody') },
                ...props.managers.map((one) => ({ value: one.id, label: one.name })),
              ],
            },
            ...(props.onTeam === undefined
              ? []
              : teamFilter(t, props.teams ?? [], props.teamId ?? '', props.onTeam)),
            {
              key: 'type',
              label: t('leave.column.type'),
              value: props.leaveTypeId,
              onChange: props.onLeaveType,
              options: [
                { value: '', label: t('leave.filter.allTypes') },
                ...props.types.map((type) => ({ value: type.id, label: type.name })),
              ],
            },
            {
              key: 'status',
              label: t('leave.column.status'),
              value: props.status,
              onChange: props.onStatus,
              options: [
                { value: '', label: t('leave.filter.allStatuses') },
                { value: 'approved', label: t('leave.status.approved') },
                { value: 'pending', label: t('leave.status.pending') },
              ],
            },
          ]}
          /* 17 Oct 2026 · the period rides the toolbar, so a phone places it beside the search. */
          trailing={
            <RangeControl
              /* ⚠️ The period ASKED for, so the control names it before the answer lands. */
              value={props.range ?? { from: props.timeline.from, to: props.timeline.to }}
              defaultRange={props.defaultRange}
              onChange={(range) => {
                props.onRange(range.from, range.to);
              }}
            />
          }
        />
      </div>

      {props.loading === true ? (
        <PeopleGridSkeleton lines={1} />
      ) : props.timeline.rows.length === 0 ? (
        <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
          <Icon name="sun" size={22} className="opacity-60" />
          <span className="text-[13px]">{t('dash.empty.leave')}</span>
        </div>
      ) : (
        <Gantt
          rows={rows}
          rangeFrom={props.timeline.from}
          rangeTo={props.timeline.to}
          unitWidth={ZOOMS[zoom].unitWidth}
          unit={ZOOMS[zoom].unit}
          today={props.today}
          readOnly
          /* ⚠️ A line between every day — 13 Oct 2026, *"line separating the days is missing"*. A
             leave grid is read day by day; `gantt.tsx` keeps it off for the tasks timeline. */
          ruleEveryDay
          /* 17 Oct 2026 · a phone keeps the days in view: the names take 132px there. */
          nameWidth={mobile ? 132 : 240}
          /* ⚠️ No second frozen column: a person's row has one identity, and a column of em-dashes
             on every row would be a column that says nothing. `gantt.tsx` takes zero for this. */
          assigneeWidth={0}
          /* 26 Oct 2026 · two lines a row — the name and, since 17 Oct, the default project role
             under it — so the two-line rows' 46, as the metafield and permission lists. At 34 the
             pair was pressed against the row's edges. */
          rowHeight={46}
          labels={{
            timeline: t('title.dashLeave'),
            name: t('dash.column.person'),
            assignee: '',
            unassigned: '',
            months: calendar.months,
            bar: (row) => `${row.title} · ${row.start} – ${row.end}`,
            today: t('tasks.today'),
            conflicts: () => '',
            empty: t('dash.empty.leave'),
          }}
          /**
           * ┌────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ THE AWAY-COUNT ABOVE THE COLUMNS IS GONE — round 5 item 7.        │
           * └────────────────────────────────────────────────────────────────┘
           *
           * *"A napok felett lévő számolót (1) ami mutatja hány nap van ott kivéve, azt vegyük le.
           * A dátum csak a dátumot mutatja."*
           *
           * ⚠️ **`columnStrip` itself stays on the chart** — it is `gantt.tsx`'s prop and the tasks
           * timeline may want it. What goes is this screen passing one: the header is a date again.
           *
           * ⚠️ **The count is not lost, it was redundant.** Every band under the column says who is
           * away, and the row count beside the filter says how many requests the window holds. A
           * digit floating over a date answered a question the bars beneath it already answer.
           */
          columnTint={(column) => {
            /* ⚠️ Only at the day unit: a month is never a weekend, and tinting a whole month
               because it contains a holiday would say the month was one. */
            if (column.days !== 1) return undefined;
            if (holidays.has(column.from)) return 'var(--muted)';
            const weekday = new Date(`${column.from}T00:00:00Z`).getUTCDay();
            return weekday === 0 || weekday === 6
              ? 'color-mix(in srgb, var(--muted) 55%, transparent)'
              : undefined;
          }}
          onOpen={(key) => {
            /**
             * ⚠️⚠️ **THE NAME COLUMN CALLS THIS TOO, WITH THE PERSON'S ID.** `gantt.tsx` made the
             * frozen name a button deliberately — a task with no dates would otherwise be
             * unreachable — so this handler receives either a band's request id or a row's member
             * id, and opening a request drawer on a member id would 404 at the person's own name.
             */
            if (requestIds.has(key)) props.onOpenRequest(key);
          }}
        />
      )}
    </div>
  );
}
